import React, { FC, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { Box, Grid, IconButton, makeStyles, Paper, SvgIcon } from '@material-ui/core';
import classNames from 'classnames';
import useRootSelector from 'common/hooks/useRootSelector';
import { selectIsMemberPortalUserLoggedIn } from 'memberPortalModules/authentication/state/selectors';
import {
  selectCurrentUserSelectedLevel,
  selectIsUserLoggedIn,
} from 'modules/authentication/state/selectors';
import { PermissionLevels } from 'common/constants/permissions';
import { Routes } from 'constants/route.constants';
import useMenuState from 'common/hooks/useMenuState';
import {
  ClubSelector,
  HeaderClubLogo,
  LanguageSelector,
  UserMenu,
  UserTimezoneSelector,
} from './components';
import { GlobalSearch, HelpInfoSearch, ContactPopover } from 'components';
import { CustomTheme } from 'common/ui/interfaces';
import { ReactComponent as Hamburger } from 'img/icons/hamburger.svg';
import { ReactComponent as HelpIcon } from 'img/icons/help.svg';
import { ReactComponent as SearchIcon } from 'img/icons/search.svg';
import { ReactComponent as TimeclockIcon } from 'img/icons/hourglass.svg';
import { ReactComponent as PhoneIcon } from 'img/icons/phone.svg';
import { ClockInModal } from 'modules/timeclock/components';
import { selectUserRole } from 'common/state/userRole/selectors';
import { useIsMobileView } from 'common/hooks/useIsMobileView';
import peakLogo from 'img/peakFullLogo.png';
import AppService from 'services/application/AppService';
import useSidebarContext from 'common/hooks/context/useSidebarContext';
import MemberPortalCart from './components/MemberPortalCart';
import EditMemberProfilePasswordModal from 'memberPortalModules/MemberProfile/components/EditMemberProfilePasswordModal/EditMemberProfilePasswordModal';
import MemberPortalHeaderClubLogo from './components/MemberPortalHeaderClubLogo';
import UserNotificationsContainer from 'components/Header/components/UserNotificationsContainer';
import MemberPortalUserNotificationsContainer from 'components/Header/components/MemberPortalUserNotificationsContainer';
import EditMemberProfilePinModal from '../../memberPortalModules/MemberProfile/components/EditMemberProfilePinModal/EditMemberProfilePinModal';

const useStyles = makeStyles((theme: CustomTheme) => ({
  iconButton: {
    width: '20px',
    height: '20px',
  },
  headerContainer: {
    '& > *:not(:last-child)': {
      boxShadow: 'unset',
      borderBottom: '1px solid #e0e0e0',
    },
  },
  header: {
    display: 'flex',
    minHeight: '70px',
    borderRadius: 'unset',
    boxShadow: theme.shadows[2],
  },
  headerItem: {
    flex: 1,
    paddingLeft: '19px',
    paddingRight: '12px',
  },
  subheader: {
    display: 'flex',
    alignItems: 'center',
    boxShadow: theme.shadows[2],
    '& > .MuiGrid-item': {
      width: '100%',
      '& #headerBtnClub': {
        width: '100%',
        paddingTop: '10px',
        paddingBottom: '10px',
        '& .MuiButton-label': {
          justifyContent: 'space-between',
        },
      },
    },
  },
}));

const Header: FC = () => {
  const { setSidebarOpen } = useSidebarContext();
  const isMobileView = useIsMobileView();
  const isDesktopView = !isMobileView;

  const {
    menuAnchorEl: globalSearchAnchorEl,
    handleShowMenu: handleShowGlobalSearch,
    handleCloseMenu: handleCloseGlobalSearch,
  } = useMenuState();

  const {
    menuAnchorEl: helpInfoSearchAnchorEl,
    handleShowMenu: handleShowHelpInfoSearch,
    handleCloseMenu: handleCloseHelpInfoSearch,
  } = useMenuState();

  const {
    menuAnchorEl: contactPopoverAnchorEl,
    handleShowMenu: handleShowContactPopover,
    handleCloseMenu: handleCloseContactPopover,
  } = useMenuState();

  const {
    menuAnchorEl: clockInModalAnchorEl,
    handleShowMenu: handleShowClockInModal,
    handleCloseMenu: handleCloseClockInModal,
  } = useMenuState();

  const userPermissionLevel: PermissionLevels = useRootSelector(selectCurrentUserSelectedLevel);
  const userRole = useRootSelector(selectUserRole);
  const clientPortalUserLogged = useRootSelector(selectIsUserLoggedIn);
  const memberPortalUserLogged = useRootSelector(selectIsMemberPortalUserLoggedIn);
  const [isOpenChangePasswordModal, setIsOpenChangePasswordModal] = useState(false);
  const [isOpenChangePinModal, setIsOpenChangePinModal] = useState(false);
  const isClientPortal = userRole === 'client';
  const isMemberPortal = userRole === 'member';
  const isClientLevel = userPermissionLevel === PermissionLevels.CLIENT;
  const isCorpLevel = userPermissionLevel === PermissionLevels.CORPORATE;
  const classes = useStyles();

  const toggleSidebar = (): void => {
    setSidebarOpen(prevSidebarOpen => {
      const sidebarOpen = !prevSidebarOpen;
      AppService.setAppSidebarOpen(sidebarOpen);

      return sidebarOpen;
    });
  };

  const getLogo = (): JSX.Element => {
    if (isClientPortal) {
      if (isClientLevel || isCorpLevel) {
        return (
          <Grid item>
            <NavLink to={Routes.HOME}>
              <HeaderClubLogo />
            </NavLink>
          </Grid>
        );
      }
    } else if (isMemberPortal) {
      return <MemberPortalHeaderClubLogo />;
    }

    return (
      <Grid item>
        <NavLink to={Routes.HOME}>
          <img height={25} src={peakLogo} alt="peak logo" />
        </NavLink>
      </Grid>
    );
  };

  const getClubSelector = (): JSX.Element | null => {
    if ((isClientPortal && isClientLevel) || isCorpLevel) {
      return (
        <Grid item>
          <ClubSelector />
        </Grid>
      );
    }

    return null;
  };

  return (
    <>
      <Box className={classes.headerContainer} position="relative" width="100%" zIndex={10}>
        <Paper className={classes.header}>
          <Box
            className={classes.headerItem}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            p={1.25}
            overflow="hidden"
          >
            <Grid container spacing={1} alignItems="center" justifyContent="space-between">
              <Grid item>
                <Grid container spacing={1} alignItems="center">
                  <Grid item>
                    <IconButton id="hamburger" onClick={toggleSidebar} color="secondary">
                      <SvgIcon component={Hamburger} />
                    </IconButton>
                  </Grid>

                  {getLogo()}
                  {isDesktopView ? getClubSelector() : null}
                </Grid>
              </Grid>

              <Grid item>
                <Grid container spacing={3} alignItems="center">
                  {isDesktopView ? (
                    <Grid item>
                      <UserTimezoneSelector />
                    </Grid>
                  ) : null}
                  {isDesktopView && isClientPortal && (isClientLevel || isCorpLevel) ? (
                    <Grid item>
                      <IconButton onClick={handleShowClockInModal} color="secondary">
                        <TimeclockIcon className={classes.iconButton} />
                      </IconButton>
                    </Grid>
                  ) : null}
                  {isClientPortal ? (
                    <Grid item>
                      <IconButton onClick={handleShowGlobalSearch} color="secondary">
                        <SearchIcon className={classes.iconButton} />
                      </IconButton>
                    </Grid>
                  ) : null}
                  <Grid item>
                    <LanguageSelector />
                  </Grid>
                  {isDesktopView ? (
                    <Grid item>
                      <IconButton onClick={handleShowContactPopover} color="secondary">
                        <PhoneIcon className={classes.iconButton} />
                      </IconButton>
                    </Grid>
                  ) : null}
                  <Grid item>
                    <IconButton
                      disabled={!isClientPortal}
                      onClick={handleShowHelpInfoSearch}
                      color="secondary"
                    >
                      <HelpIcon className={classes.iconButton} />
                    </IconButton>
                  </Grid>
                  {isDesktopView && !isClientPortal ? (
                    <Grid item>
                      <MemberPortalCart />
                    </Grid>
                  ) : null}
                  {isDesktopView ? (
                    <Grid item>
                      {clientPortalUserLogged ? <UserNotificationsContainer /> : null}
                      {memberPortalUserLogged ? <MemberPortalUserNotificationsContainer /> : null}
                    </Grid>
                  ) : null}
                  <Grid item>
                    <UserMenu
                      onChangePasswordClick={() => setIsOpenChangePasswordModal(true)}
                      onChangePinClick={() => setIsOpenChangePinModal(true)}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </Paper>

        {isMobileView ? (
          <div className={classNames('subheader', classes.subheader)}>{getClubSelector()}</div>
        ) : null}
      </Box>

      {!!globalSearchAnchorEl && (
        <GlobalSearch anchorEl={globalSearchAnchorEl} onClose={handleCloseGlobalSearch} />
      )}

      {!!helpInfoSearchAnchorEl && (
        <HelpInfoSearch anchorEl={helpInfoSearchAnchorEl} onClose={handleCloseHelpInfoSearch} />
      )}

      {!!contactPopoverAnchorEl && (
        <ContactPopover
          isClientPortal={isClientPortal}
          currentUserLevel={userPermissionLevel}
          anchorEl={contactPopoverAnchorEl}
          onClose={handleCloseContactPopover}
        />
      )}

      {!!clockInModalAnchorEl && (
        <ClockInModal anchorEl={clockInModalAnchorEl} onClose={handleCloseClockInModal} />
      )}

      {isOpenChangePasswordModal && (
        <EditMemberProfilePasswordModal onClose={() => setIsOpenChangePasswordModal(false)} />
      )}

      {isOpenChangePinModal && (
        <EditMemberProfilePinModal onClose={() => setIsOpenChangePinModal(false)} />
      )}
    </>
  );
};

export default Header;
